import type { GospelMonth } from '../api'
import { compareYen, subtractYen } from '../format'

// The pipeline/booked split the forecast charts are drawn on, and where the
// reporting stops.
//
// `forecast_net_new_ar` is weighted pipeline: deals not yet signed, times the
// sheet's FCF conversion rate. Per A-01 that rate leans margin, not win
// probability, so a line that includes it reads close to "every open deal
// closes". It lands in net_cash_difference for EVERY month it covers, reported
// months included, so it is not a tail the charts can cut off: it has to be
// subtracted month by month. The charts default to the booked view (booked AR,
// closed fundraise, payroll actual or planned, planned costs) and add the
// pipeline only on request.
//
// Zero-tests go through compareYen, the exact BigInt comparison in format.ts,
// never a float: `0.00` and `-0.00` and `0` must all read as "no pipeline" and
// none of them may arrive as 1e-15.

/** Does weighted pipeline put money into this month? */
export function hasPipeline(month: GospelMonth): boolean {
  return compareYen(month.forecastNetNewAr, '0') !== 0
}

/**
 * The month's net without the weighted pipeline, exactly (decimal strings, no
 * float). Equal to cash_position_by_month.net_cash_difference_ex_pipeline.
 */
export function netExPipeline(month: GospelMonth): string {
  return subtractYen(month.netCashDifference, month.forecastNetNewAr)
}

/**
 * The first month whose payroll is NOT reported (YYYY-MM-01), or null when every
 * month in range is. From here the running cash position stops being a record.
 *
 * Deliberately 'plan' OR 'missing': a balance is cumulative, so once a month
 * goes unreported every later balance inherits the gap — including months whose
 * own payroll came back. Marking only the unreported months would let a fully
 * reported month sit on a line that is already short a payroll and look sound.
 * What this boundary must NOT claim is that the plan is carrying the line: for a
 * 'missing' month nothing is, which is why the wording downstream says forecast
 * rather than plan.
 */
export function firstUnreportedMonth(months: GospelMonth[]): string | null {
  return months.find((month) => month.apBasis !== 'actual')?.month ?? null
}
